import { Component, Inject, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { ActionFailed, compactObject, positive, SaveForm, Toaster } from '@tokeet-frontend/tv3-platform'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import {
  BdcContentV2Service,
  BdcUnitMetadataUnitNameItem,
  BdcUnitResponse,
  BdcProductResponse,
  BdcPolicy,
  BdcRatePlanResponse,
  bdcMealPlanCodes,
} from '@tokeet-frontend/channel-content'
import * as lodash from 'lodash'
import { finalize, map, tap } from 'rxjs/operators'
import { combineLatest } from 'rxjs'
import { Store } from '@ngrx/store'
import { MatSelectChange } from '@angular/material/select'

export interface BDCPropertyProductDialogParams {
  propertyId: number
  product?: BdcProductResponse
}

@Component({
  selector: 'app-bdc-property-product-dialog',
  templateUrl: './bdc-property-product-dialog.component.html',
  styleUrls: ['./bdc-property-product-dialog.component.scss'],
  host: {
    class: 'modal-content',
  },
})
export class BdcPropertyProductDialogComponent implements OnInit {
  form = this.fb.group({
    rateId: [],
    roomId: [],
    mealPlanCode: [],
    policyCode: [],
    minAdvancedBookingOffset: [null, [positive()]],
    minAdvancedBookingOffsetType: ['D'],
    maxAdvancedBookingOffset: [null, [positive()]],
    maxAdvancedBookingOffsetType: ['D'],
  })
  advancedBookingOffsetTypes = [
    { label: 'Year', value: 'Y' },
    { label: 'Month', value: 'M' },
    { label: 'Day', value: 'D' },
    { label: 'Hour', value: 'H' },
  ]

  mealPlanActiveCtrl = new FormControl(false)
  mealPlansBreakfastCtrl = new FormControl(false)
  mealPlansLunchCtrl = new FormControl(false)
  mealPlansDinnerCtrl = new FormControl(false)

  mealPlanCodes = bdcMealPlanCodes
  rates: BdcRatePlanResponse[] = []
  filteredRates: BdcRatePlanResponse[] = []
  products: BdcProductResponse[] = []
  units: BdcUnitResponse[] = []
  unitNamesById: Record<number, BdcUnitMetadataUnitNameItem> = {}
  cancelPolicies: BdcPolicy[] = []
  isEdit = false
  isLoading = true

  constructor(
    private fb: FormBuilder,
    private contentService: BdcContentV2Service,
    private store: Store<any>,
    private toaster: Toaster,
    public dialogRef: MatDialogRef<BdcPropertyProductDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BDCPropertyProductDialogParams
  ) {}

  ngOnInit() {
    const product = this.data.product
    if (product) {
      this.isEdit = true
      const minAdvancedBookingOffset = product.minAdvancedBookingOffset
      const maxAdvancedBookingOffset = product.maxAdvancedBookingOffset
      this.form.patchValue({
        ...product,
        minAdvancedBookingOffset: +lodash.trim(minAdvancedBookingOffset, 'PHDMY') || null,
        // minAdvancedBookingOffsetType: lodash.last(minAdvancedBookingOffset) || 'H',
        maxAdvancedBookingOffset: +lodash.trim(maxAdvancedBookingOffset, 'PHDMY') || null,
        // maxAdvancedBookingOffsetType: lodash.last(maxAdvancedBookingOffset) || 'H',
      })
      this.setMealPlans(product.mealPlanCode)
      this.form.get('roomId')?.disable()
      this.form.get('rateId')?.disable()
    }

    combineLatest([
      this.contentService
        .getPropertyRatePlans(this.data.propertyId)
        .pipe(map((rates) => rates.filter((r) => !!r.active))),
      this.contentService.getUnitMetadata(),
      this.contentService.getPropertyUnits(this.data.propertyId),
      this.contentService.getPropertyProducts(this.data.propertyId),
      this.contentService.getPropertyPolicies(this.data.propertyId),
    ]).subscribe(([rates, metadata, units, products, policies]) => {
      this.isLoading = false
      this.units = units
      this.unitNamesById = lodash.keyBy(metadata.unit_names, (t) => t.unit_name_id)
      this.rates = rates
      this.products = products
      this.refreshRates(product?.roomId)
      this.cancelPolicies = policies

      if (!this.rates.length) {
        this.toaster.warning($localize`There is no available rate for new product. please create a new rate.`)
      }
    })
  }

  setMealPlans(mealPlanCode: number) {
    if (mealPlanCode) {
      const mealPlanCodes = this.getMealPlanCodes(mealPlanCode)
      this.mealPlansBreakfastCtrl.setValue(mealPlanCodes.includes(19))
      this.mealPlansLunchCtrl.setValue(mealPlanCodes.includes(21))
      this.mealPlansDinnerCtrl.setValue(mealPlanCodes.includes(22))
      this.mealPlanActiveCtrl.setValue(true)
    } else {
      this.mealPlansBreakfastCtrl.setValue(false)
      this.mealPlansLunchCtrl.setValue(false)
      this.mealPlansDinnerCtrl.setValue(false)
      this.mealPlanActiveCtrl.setValue(false)
    }
  }

  getMealPlanCodes(code: number) {
    // Breakfast -> 19
    // Breakfast && Lunch -> 23
    // Breakfast && Dinner -> 12
    // Lunch -> 21
    // Lunch && Dinner -> 24
    // Dinner -> 22
    // All-inclusive -> 1
    const mealPlanCodes = []
    if (code == 19) mealPlanCodes.push(19)
    if (code == 21) mealPlanCodes.push(21)
    if (code == 22) mealPlanCodes.push(22)

    if (code == 23) mealPlanCodes.push(19, 21)
    if (code == 12) mealPlanCodes.push(19, 22)
    if (code == 24) mealPlanCodes.push(21, 22)
    if (code == 1) mealPlanCodes.push(19, 21, 22)
    return mealPlanCodes
  }

  getCombinedMealPlanCodes() {
    const isBreakfast = this.mealPlansBreakfastCtrl.value
    const isLunch = this.mealPlansLunchCtrl.value
    const isDinner = this.mealPlansDinnerCtrl.value
    if (isBreakfast && isLunch && isDinner) return 1
    if (isBreakfast && isLunch) return 23
    if (isBreakfast && isDinner) return 12
    if (isLunch && isDinner) return 24
    if (isBreakfast) return 19
    if (isLunch) return 21
    if (isDinner) return 22
    return 0
  }

  getUsedRateIds(roomId: number) {
    return lodash.map(
      this.products.filter((p) => p.roomId === roomId),
      (p) => p.rateId
    )
  }

  onRoomChange(event: MatSelectChange) {
    const roomId = event.value
    this.refreshRates(roomId)
    this.form.get('rateId')?.reset()
  }

  refreshRates(roomId: number) {
    const usedRateIds = this.getUsedRateIds(roomId)
    this.filteredRates = this.isEdit
      ? this.rates.filter((rate) => rate.id === this.data.product?.rateId || !lodash.includes(usedRateIds, rate.id))
      : this.rates.filter((rate) => !lodash.includes(usedRateIds, rate.id))
  }

  close() {
    this.dialogRef.close()
  }

  getPayload() {
    const formData = this.form.getRawValue()
    return compactObject(
      {
        roomId: formData.roomId,
        rateId: formData.rateId,
        policyCode: formData.policyCode,
        mealPlanCode: this.mealPlanActiveCtrl.value ? this.getCombinedMealPlanCodes() : 0,
        minAdvancedBookingOffset: lodash.isNumber(formData.minAdvancedBookingOffset)
          ? `P${formData.minAdvancedBookingOffset || 0}${formData.minAdvancedBookingOffsetType}`
          : null,
        maxAdvancedBookingOffset: lodash.isNumber(formData.maxAdvancedBookingOffset)
          ? `P${formData.maxAdvancedBookingOffset || 0}${formData.maxAdvancedBookingOffsetType}`
          : null,
      },
      ['mealPlanCode']
    )
  }

  create() {
    return this.contentService.createPropertyProducts(this.data.propertyId, this.getPayload()).pipe(
      tap(() => {
        this.toaster.success($localize`Product added successfully.`)
      })
    )
  }

  update() {
    const formData = this.form.getRawValue()
    return this.contentService
      .updatePropertyProduct(this.data.propertyId, formData.roomId, formData.rateId, this.getPayload())
      .pipe(
        tap(() => {
          this.toaster.success($localize`Product updated successfully.`)
        })
      )
  }

  @SaveForm()
  onSave(form: FormGroup) {
    this.isLoading = true
    if (this.isEdit) {
      return this.update()
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe(
          () => {
            this.dialogRef.close('refresh')
          },
          (error) => {
            this.store.dispatch(ActionFailed({ error }))
          }
        )
    } else {
      this.create()
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe(
          () => {
            this.dialogRef.close('refresh')
          },
          (error) => {
            this.store.dispatch(ActionFailed({ error }))
          }
        )
    }
  }
}
